import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '../../global.css';
import NeuralCanvas from '../../components/NeuralCanvas';
import GoogleButton from '../../components/GoogleButton';
import WordmarkLogo from '../../components/WordmarkLogo';
import AuthFooter from '../../components/AuthFooter';
import { registerUser, socialAuth, clearError } from '../../store/slices/authSlice';

export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, accessToken, requiresVerification } = useSelector(s => s.auth);
  const [form, setForm] = useState({ email: '', name: '', password: '' });

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (!accessToken) return;
    if (requiresVerification) navigate('/verify-email');
    else navigate('/dashboard');
  }, [accessToken, requiresVerification, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const [firstName, ...rest] = form.name.trim().split(' ');
    dispatch(registerUser({
      first_name: firstName,
      last_name: rest.join(' ') || firstName,
      email: form.email,
      password: form.password,
    }));
  };

  return (
    <div className="auth-layout">
      <NeuralCanvas />

      <div className="signup-shell">
      <div className="signup-left">
        <WordmarkLogo size={22} />
        <div style={{ margin: 'auto 0', display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 440 }}>
          <div className="dash-sub">GET STARTED</div>
          <h1 className="signup-title">Join Africa's<br /><em>AI platform.</em></h1>
          <p className="signup-sub">Start for free. No credit card. Use everything from day one — healthcare, agriculture, finance, safety, and more.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 28, borderTop: '1px solid var(--line)' }}>
            {[
              ['01', 'Built for your world', 'Healthcare, agriculture, finance, safety — AI that understands African realities, not Western assumptions.'],
              ['02', 'Right where you are', 'Data centres in Lagos, Nairobi, and Johannesburg. Fast responses. Your data stays in Africa.'],
              ['03', 'Start free, grow anytime', 'No setup fees, no credit card, no demo calls. Scale up only when you\'re ready.'],
            ].map(([n, t, d]) => (
              <div key={n} style={{ display: 'flex', gap: 16 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.08em', flexShrink: 0, paddingTop: 2 }}>{n}</div>
                <div>
                  <div style={{ fontSize: 14, color: 'var(--fg)', marginBottom: 4 }}>{t}</div>
                  <div style={{ fontSize: 13, color: 'var(--fg-mid)', lineHeight: 1.55 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <div className="signup-right">
        <form className="signup-form" onSubmit={handleSubmit}>
          <div>
            <div className="dash-sub" style={{ marginBottom: 10 }}>CREATE ACCOUNT</div>
            <h2 className="signup-title" style={{ fontSize: 30 }}>Join the<br /><em>platform.</em></h2>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
              <button className="alert-dismiss" onClick={() => dispatch(clearError())} aria-label="Dismiss">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          )}

          <GoogleButton
            label="Continue with Google"
            onSuccess={(profile) => dispatch(socialAuth({ provider: 'google', ...profile }))}
          />
          <div className="auth-divider">or</div>

          <div className="field">
            <label>Full name</label>
            <input type="text" required placeholder="Dami Adebayo"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="field">
            <label>Email</label>
            <input type="email" required placeholder="dami@studio.lagos"
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="field">
            <label>Password</label>
            <input type="password" required placeholder="8+ characters"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          </div>

          <button type="submit" className="btn btn-primary btn-lg" style={{ justifyContent: 'center', marginTop: 4 }} disabled={loading}>
            {loading ? <><span className="btn-loader" /> Creating account…</> : 'Create account →'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <p style={{ color: 'var(--fg-dim)', lineHeight: 1.6 }}>
              By signing up you agree to our{' '}
              <Link to="/terms" style={{ color: 'var(--fg-mid)', textDecoration: 'underline' }}>terms</Link> and{' '}
              <Link to="/legal" style={{ color: 'var(--fg-mid)', textDecoration: 'underline' }}>privacy</Link>.
            </p>
            <Link to="/login" style={{ color: 'var(--accent)', flexShrink: 0, marginLeft: 16 }}>Sign in →</Link>
          </div>
        </form>
      </div>
      </div>

      <AuthFooter />
    </div>
  );
}
